import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Divider,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import {
  BadgeCheck,
  BadgeInfo,
  BadgeX,
  Calendar,
  Clock,
  ArrowLeftRight,
} from "lucide-react";
import { useDispatch } from "react-redux";
import ReusableAvatar from "components/Avatar/ReusableAvatar";
import InfoCard from "components/Card/InfoCard";
import { formatBWP } from "../utils/formatBWP";
import { formatDate } from "utils/dateFormatter";
import BackgroundImage from "../../../../assets/img/back.png";
import GradientButton from "components/Buttons/GradientButton";
import RefundModal from "./RefundModal";
import { refundTransactionAction } from "state/actions/transactionActions";

// Reusable component for label-value rows
const DetailRow = ({ label, value }) => (
  <Flex justifyContent="space-between" mb={2} color="gray.500" fontSize="sm">
    <Text>
      <strong>{label}</strong>
    </Text>
    <Text>{value}</Text>
  </Flex>
);

// Reusable badge component for status
const StatusBadge = ({ status }) => {
  const colorScheme =
    status === "Completed"
      ? "green"
      : status === "Pending"
        ? "yellow"
        : status === "Failed"
          ? "red"
          : status === "Refunded"
            ? "blue"
            : "gray";


  return (
    <Badge
      colorScheme={colorScheme}
      variant="outline"
      borderRadius="8px"
      height="20px"
      px="8px"
    >
      {status}
    </Badge>
  );
};

// Custom tab with icon and label
const CustomTab = ({ icon, label }) => (
  <Tab
    fontSize="sm"
    color="gray.500"
    _selected={{
      color: "white",
      bg: "transparent",
      backgroundImage: `url(${BackgroundImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <Icon as={icon} mr={2} />
    {label}
  </Tab>
);

const ViewTransaction = ({ transaction, onReload }) => {
  const [isRefundModalOpen, setRefundModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [localStatus, setLocalStatus] = useState(transaction.transaction_status);

  useEffect(() => {
    setLocalStatus(transaction.transaction_status);
  }, [transaction.transaction_status]);

  const openRefundModal = () => setRefundModalOpen(true);
  const closeRefundModal = () => setRefundModalOpen(false);

  const handleRefund = async ({ paygateTransactionId }) => {
    try {
      const result = await dispatch(refundTransactionAction(paygateTransactionId));

      if (result.success) {
        setLocalStatus("Refunded"); 
        if (onReload) {
          onReload();
        }
      }

      return result;
    } catch (error) {
      return { success: false, error: error.message || "Refund failed" };
    }
  };


  if (!transaction) return null;

  const calculateOurFee = (amount) => amount * 0.05;

  return (
    <Flex direction="column" p={8} width="100%">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center">
          <ReusableAvatar name={transaction?.package_name} />
          <Box ml={4}>
            <Text
              fontSize="2xl"
              fontWeight="bold"
              display="flex"
              alignItems="center"
            >
              {transaction.package_name}
              {localStatus=== "Completed" && (
                <>
                  <Icon as={BadgeCheck} size={12} color="green.500" ml={1} />
                  <Text color="green.500" fontSize="13px" ml={1}>
                    Completed
                  </Text>
                </>
              )}
              {localStatus=== "Failed" && (
                <>
                  <Icon as={BadgeX} size={12} color="red.500" ml={1} />
                  <Text color="red.500" fontSize="13px" ml={1}>
                    Failed
                  </Text>
                </>
              )}
              {localStatus=== "Pending" && (
                <>
                  <Icon as={BadgeInfo} size={12} color="orange.500" ml={1} />
                  <Text color="orange.500" fontSize="13px" ml={1}>
                    Pending
                  </Text>
                </>
              )}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {transaction.sender_name} •{" "}
              <Text as="span" fontWeight="bold" color="gray.500">
                {formatBWP(transaction.amount)}
              </Text>
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Dates row */}
      <Flex mt={2} alignItems="center">
        <Icon as={Calendar} size={16} color="gray.500" mr={1} />
        <Text fontSize="13px" color="gray.500" mr={4}>
          Transaction Date: {formatDate(transaction.transaction_date)}
        </Text>
        <Icon as={Clock} size={16} color="gray.500" mr={1} />
        <Text fontSize="13px" color="gray.500">
          Completion Date:{" "}
          {transaction.completion_date
            ? formatDate(transaction.completion_date)
            : "Transaction not completed"}
        </Text>
      </Flex>

      <Divider my={6} />

      {/* Tabs */}
      <Tabs variant="enclosed">
        <TabList>
          <CustomTab icon={ArrowLeftRight} label="Transaction Details" />
          <CustomTab icon={BadgeInfo} label="Resolve Payment Issues" />
        </TabList>

        <TabPanels>
          {/* Transaction Details Tab */}
          <TabPanel>
            <InfoCard
              heading="Transaction Details"
              icon={<ArrowLeftRight />}
              headingFontSize="md"
              headingFontWeight="500"
              headingColor="gray.500"
            >
              <DetailRow
                label="Sender Name"
                value={
                  transaction.sender_contact
                    ? `${transaction.sender_name} (${transaction.sender_contact})`
                    : transaction.sender_name
                }
              />
              <DetailRow
                label="Driver Name"
                value={
                  transaction.driver_contact
                    ? `${transaction.driver_name} (${transaction.driver_contact})`
                    : transaction.driver_name
                }
              />
              <DetailRow label="Package Name" value={transaction.package_name} />
              <Flex
                justifyContent="space-between"
                mb={2}
                color="gray.500"
                fontSize="sm"
              >
                <Text>
                  <strong>Status</strong>
                </Text>
                <StatusBadge status={localStatus} />
              </Flex>
              <DetailRow
                label="Transaction Date"
                value={formatDate(transaction.transaction_date)}
              />
              <DetailRow
                label="Completion Date"
                value={
                  transaction.completion_date
                    ? formatDate(transaction.completion_date)
                    : "Transaction not completed"
                }
              />
              <DetailRow label="Payment Method" value={transaction.payment_method} />
              <DetailRow label="Amount" value={formatBWP(transaction.amount)} />
              <DetailRow
                label="Driver Commission"
                value={formatBWP(transaction.commission)}
              />
              <DetailRow
                label="Paygate Commission"
                value={formatBWP(transaction.amount * 0.033)}
              />
              <DetailRow
                label="Our Fee"
                value={formatBWP(calculateOurFee(transaction.amount))}
              />
              {/* <DetailRow
                label="Payment Gateway Reference"
                value={transaction.payment_gateway_reference}
              /> */}
            </InfoCard>
          </TabPanel>

          {/* Resolve Payment Issues Tab */}
          <TabPanel>
            <InfoCard
              heading="Resolve Payment Issues"
              icon={<BadgeInfo />}
              headingFontSize="md"
              headingFontWeight="500"
              headingColor="gray.500"
            >
              <Text mb={4} color="gray.600" fontSize="sm">
                If this transaction encountered a payment issue, you can manually
                process a refund or mark the issue as resolved.
              </Text>

              <Flex gap={4} mt={4} direction={{ base: "column", md: "row" }}>
                <Box
                  flex="1"
                  p={4}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                >
                  <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>
                    Refund Options
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={3}>
                    Choose the refund method for the user in case of a failed or
                    disputed transaction.
                  </Text>
                  <Flex justifyContent={"end"}>
                    <GradientButton
                      fontSize="xs"
                      width="auto"
                      height="40px"
                      onClick={openRefundModal}
                    >
                      Refund to Card
                    </GradientButton>
                    {/* <GradientButton
                      fontSize="xs"
                      width="auto"
                      height="40px"
                      bg="gray.200"
                      color="gray.800"
                      _hover={{ bg: "gray.300" }}
                      onClick={() => {
                        // TODO: manual refund logic
                        console.log("Manual refund");
                      }}
                    >
                      Manual Refund
                    </GradientButton> */}
                  </Flex>
                </Box>

                {/* <Box
                  flex="1"
                  p={4}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                >
                  <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>
                    Mark as Resolved
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={3}>
                    If the issue was resolved outside this platform, you can manually
                    mark the transaction as resolved.
                  </Text>
                  <GradientButton
                    fontSize="xs"
                    width="auto"
                    height="40px"
                    onClick={() => {
                      // TODO: mark as resolved logic
                      console.log("Marked as resolved");
                    }}
                  >
                    Mark Resolved
                  </GradientButton>
                </Box> */}
              </Flex>
            </InfoCard>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Refund Modal */}
      <RefundModal
        isOpen={isRefundModalOpen}
        onClose={closeRefundModal}
        paygateTransactionId={transaction.paygate_transaction_id}
        refundAmount={transaction.amount}
        status={transaction.transaction_status}
        onRefund={handleRefund}
      />
    </Flex>
  );
};

export default ViewTransaction;
